import {
  ActivationRequiredAlert,
  RegistrationSuccessAlert,
  SecondaryEmailExistsAlert,
  UnexpectedErrorAlert,
  UserAlreadyExistsAlert,
} from '@/components/alerts';
import { ContactSourceSelector } from '@/components/ContactSourceSelector';
import { CountrySelector } from '@/components/CountrySelector';
import { CustomInput } from '@/components/CustomInput';
import { OptInInput } from '@/components/OptOutInput';
import { PhoneInput } from '@/components/PhoneInput';
import { ServiceSelector } from '@/components/ServiceSelector';
import { SubmitButton } from '@/components/SubmitButton';
import { TurnstileField } from '@/components/TurnstileField';
import { EMAIL_REGEX, EMAIL_VALIDATION_MESSAGE } from '@/constants';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useTurnstile } from '@/hooks/useTurnstile';
import { AccountCenterBox } from '@/layout/AccountCenterBox';
import { api } from '@/services/api';
import { validatePhoneNumber } from '@/utils/phoneUtils';
import { countries } from '@shared/constants';
import { RegisterResponse, RegisterResultEnum } from '@shared/types';
import { AxiosResponse } from 'axios';
import { useRef } from 'react';
import { Field, Form, Formik } from 'formik';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';

const E164_REGEX = /^\+[1-9]\d{1,14}$/;

type PostalCodeField = { postalcode: { label: string } };

const isPostalCodeField = (field: object): field is PostalCodeField =>
  'postalcode' in field && typeof (field as PostalCodeField).postalcode?.label === 'string';

const getPostalCodeLabel = (countryIso: string): string => {
  const country = countries.find((c) => c.iso === countryIso);
  const postalField = country?.address?.find(isPostalCodeField);
  if (postalField) return postalField.postalcode.label;
  if (!countryIso) return 'ZIP Code';
  return 'Postal Code';
};

const countryHasPostalCode = (countryIso: string): boolean => {
  if (!countryIso) return true;
  const country = countries.find((c) => c.iso === countryIso);
  if (!country?.address) return false;
  return country.address.some(isPostalCodeField);
};

const registerSchema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  country: Yup.string().required('Country of residence is required'),
  postalCode: Yup.mixed().when('country', {
    is: (country: string) => countryHasPostalCode(country),
    then: () =>
      Yup.string()
        .matches(/^\d+$/, 'Invalid format for this country')
        .required('Postal code is required'),
    otherwise: () => Yup.string().notRequired(),
  }),
  email: Yup.string()
    .trim()
    .matches(EMAIL_REGEX, EMAIL_VALIDATION_MESSAGE)
    .transform((value) => value.trim())
    .required('A valid email is required'),
  phoneCountry: Yup.string(),
  phoneNumber: Yup.string().required('A valid phone number is required'),
  serviceInterest: Yup.string().required('Please select a service'),
  contactSource: Yup.string().required('Please select how you heard about us'),
  website: Yup.string(),
});

enum FormStatus {
  NoAlert = 'NoAlert',
  RegistrationSuccess = 'RegistrationSuccess',
  UserAlreadyExists = 'UserAlreadyExists',
  ActivationRequired = 'ActivationRequired',
  SystemError = 'SystemError',
  SecondaryEmailExists = 'SecondaryEmailExists',
}

type FormStatusState = {
  type: FormStatus;
  primaryEmail?: string;
};

export const Register = () => {
  useDocumentTitle('Marshall Reddick - Sign Up');
  const formLoadedAt = useRef<number>(Date.now());
  const { getToken, reset, widgetProps, widgetError } = useTurnstile();
  const getAlert = (status: FormStatusState): React.ReactNode => {
    switch (status.type) {
      case FormStatus.RegistrationSuccess:
        return <RegistrationSuccessAlert />;
      case FormStatus.UserAlreadyExists:
        return <UserAlreadyExistsAlert />;
      case FormStatus.ActivationRequired:
        return <ActivationRequiredAlert />;
      case FormStatus.SystemError:
        return <UnexpectedErrorAlert />;
      case FormStatus.SecondaryEmailExists:
        return <SecondaryEmailExistsAlert email={status.primaryEmail} />;
      default:
        return null;
    }
  };

  return (
    <Formik
      initialValues={{
        firstName: '',
        lastName: '',
        country: '',
        postalCode: '',
        email: '',
        phoneCountry: 'US',
        phoneNumber: '',
        serviceInterest: '',
        contactSource: '',
        website: '',
        turnstileToken: '',
      }}
      initialStatus={{ type: FormStatus.NoAlert } as FormStatusState}
      validationSchema={registerSchema}
      validateOnMount={false}
      onSubmit={async (values, { setStatus, setFieldError, setFieldValue }) => {
        if (values.website) {
          setStatus({ type: FormStatus.RegistrationSuccess });
          return;
        }

        const minMs = parseInt(import.meta.env.VITE_MIN_SUBMIT_MS || '4000', 10);
        console.log('Time since form loaded (ms):', Date.now() - formLoadedAt.current);
        if (Date.now() - formLoadedAt.current < minMs) {
          setStatus({ type: FormStatus.SystemError });
          return;
        }

        let inValidForm = false;
        if (!values.email.trim()) {
          setFieldError('email', 'A valid email is required');
          inValidForm = true;
        }

        if (!values.phoneNumber.trim()) {
          setFieldError('phoneNumber', 'A valid phone number is required');
          inValidForm = true;
        } else {
          if (!validatePhoneNumber(values.phoneNumber, values.phoneCountry)) {
            setFieldError('phoneNumber', 'A valid phone number is required');
            inValidForm = true;
          }
        }

        if (!values.serviceInterest.trim()) {
          setFieldError('serviceInterest', 'Please select a service');
          inValidForm = true;
        }

        if (!values.contactSource.trim()) {
          setFieldError('contactSource', 'Please select how you heard about us');
          inValidForm = true;
        }

        // phoneCountry: "US" -> callingCode: "1"
        const callingCode = countries.find((c) => c.iso === values.phoneCountry)?.phone ?? '';
        if (!callingCode) {
          setStatus({ type: FormStatus.SystemError });
          return;
        }
        const phone_e164 = `+${callingCode}${values.phoneNumber.replace(/\D/g, '')}`;
        if (!E164_REGEX.test(phone_e164)) {
          setStatus({ type: FormStatus.SystemError });
          return;
        }

        if (inValidForm) {
          return;
        }

        const turnstileToken = await getToken();

        if (!turnstileToken) {
          setFieldValue('turnstileToken', '');
          setFieldError('turnstileToken', 'Security check failed, please try again');
          reset();
          return;
        }

        setStatus({ type: FormStatus.NoAlert });

        try {
          const response: AxiosResponse<RegisterResponse> = await api.post('/auth/register', {
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            phone: values.phoneNumber,
            phoneCountryCode: values.phoneCountry,
            phone_e164,
            primaryAddressCountry: values.country,
            primaryAddressPostalCode: values.postalCode,
            serviceInterest: values.serviceInterest,
            contactSource: values.contactSource,
            turnstileToken,
          });

          const { result, primaryEmail } = response.data;
          if (result === RegisterResultEnum.SUCCESS) {
            setStatus({ type: FormStatus.RegistrationSuccess });
          } else if (result === RegisterResultEnum.USER_ALREADY_EXISTS) {
            setStatus({ type: FormStatus.UserAlreadyExists });
          } else if (result === RegisterResultEnum.UNVERIFIED) {
            setStatus({ type: FormStatus.ActivationRequired });
          } else if (result === RegisterResultEnum.SECONDARY_EMAIL_EXISTS) {
            setStatus({ type: FormStatus.SecondaryEmailExists, primaryEmail });
          } else {
            setStatus({ type: FormStatus.SystemError });
          }
        } catch {
          setStatus({ type: FormStatus.SystemError });
        } finally {
          reset();
        }
      }}
    >
      {({ values, errors, touched, isSubmitting, status, setFieldValue, setFieldTouched }) => {
        const formStatus = status as FormStatusState;

        // Show only the success alert when registration is successful
        if (formStatus.type === FormStatus.RegistrationSuccess) {
          return (
            <AccountCenterBox title="Create an Account" isSubmitting={isSubmitting}>
              {getAlert(formStatus)}
            </AccountCenterBox>
          );
        }

        return (
          <AccountCenterBox title="Create an Account" isSubmitting={isSubmitting}>
            <Form noValidate autoComplete="off">
              {/* Honeypot — hidden from real users, bots will fill this in */}
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden',
                }}
              >
                <Field type="text" name="website" tabIndex={-1} autoComplete="new-password" />
              </div>
              {getAlert(formStatus)}
              <div className="flex flex-col gap-5">
                <div className="flex flex-row gap-4">
                  <div className="form-control flex-1">
                    <Field
                      type="text"
                      name="firstName"
                      as={CustomInput}
                      label="First Name"
                      error={errors.firstName}
                      touched={touched.firstName}
                    />
                  </div>
                  <div className="form-control flex-1">
                    <Field
                      type="text"
                      name="lastName"
                      as={CustomInput}
                      label="Last Name"
                      error={errors.lastName}
                      touched={touched.lastName}
                    />
                  </div>
                </div>
                <div className="flex flex-row gap-4">
                  <div className="flex-1">
                    <CountrySelector
                      value={values.country}
                      onChange={(value) => {
                        setFieldValue('country', value);
                        setFieldValue('phoneCountry', value);
                        if (!countryHasPostalCode(value)) {
                          setFieldValue('postalCode', '');
                          setFieldTouched('postalCode', false);
                        }
                      }}
                      onBlur={() => setFieldTouched('country', true)}
                      error={errors.country}
                      touched={touched.country}
                    />
                  </div>
                  <div className="form-control flex-1">
                    <Field
                      type="text"
                      name="postalCode"
                      as={CustomInput}
                      label={getPostalCodeLabel(values.country)}
                      error={errors.postalCode}
                      touched={touched.postalCode}
                      disabled={!countryHasPostalCode(values.country)}
                    />
                  </div>
                </div>
                <div className="w-full">
                  <Field
                    type="email"
                    name="email"
                    as={CustomInput}
                    label="Email Address"
                    error={errors.email}
                    touched={touched.email}
                  />
                </div>
                <div className="flex flex-row gap-4">
                  <PhoneInput
                    phoneCountry={values.phoneCountry}
                    phoneNumber={values.phoneNumber}
                    onPhoneCountryChange={(value) => setFieldValue('phoneCountry', value)}
                    onPhoneNumberChange={(value) => setFieldValue('phoneNumber', value)}
                    onBlur={() => setFieldTouched('phoneNumber', true)}
                    error={errors.phoneNumber}
                    touched={touched.phoneNumber}
                  />
                </div>
                <div className="w-full">
                  <ServiceSelector
                    value={values.serviceInterest}
                    onChange={(value) => {
                      setFieldValue('serviceInterest', value);
                      setFieldTouched('serviceInterest', true, false);
                    }}
                    onBlur={() => setFieldTouched('serviceInterest', true)}
                    error={errors.serviceInterest}
                    touched={touched.serviceInterest}
                  />
                </div>
                <div className="w-full">
                  <ContactSourceSelector
                    value={values.contactSource}
                    onChange={(value) => {
                      setFieldValue('contactSource', value);
                      setFieldTouched('contactSource', true, false);
                    }}
                    onBlur={() => setFieldTouched('contactSource', true)}
                    error={errors.contactSource}
                    touched={touched.contactSource}
                  />
                </div>
                <OptInInput />
                <div className="text-sm mt-2 text-center text-default">
                  By signing up, you agree to our{' '}
                  <a
                    href={`${import.meta.env.VITE_WEBSITE_URL}/terms-and-conditions`}
                    target="_blank"
                    rel="noreferrer"
                    className="link text-link no-underline"
                  >
                    Terms and Conditions
                  </a>{' '}
                  and{' '}
                  <a
                    href={`${import.meta.env.VITE_WEBSITE_URL}/privacy-policy`}
                    target="_blank"
                    rel="noreferrer"
                    className="link text-link no-underline"
                  >
                    Privacy Policy
                  </a>
                </div>
              </div>

              <TurnstileField
                widgetProps={widgetProps}
                widgetError={widgetError}
                error={errors.turnstileToken}
              />
              <SubmitButton
                text="Create Account"
                className="mt-6"
                isSubmitting={isSubmitting}
                disabled={isSubmitting}
              />

              <p className="text-center mt-4 text-default text-sm">
                Already have an account?{' '}
                <Link to="/login" className="link text-link no-underline">
                  Log In
                </Link>
              </p>
            </Form>
          </AccountCenterBox>
        );
      }}
    </Formik>
  );
};
